import { Delete, Edit, LocationOn } from '@mui/icons-material';
import { Autocomplete, Box, Button, FormControl, Grid, IconButton, InputLabel, TextField, TextFieldProps, Typography, debounce } from '@mui/material';
import { AppModels, AppState, GoogleLocationInfo, LocationAttrs, brandNewLocation, fetchLocation, jsonGet, locationSelector, locationSlice, saveModel } from 'cdx-common';
import React from 'react';
import { useSelector } from 'react-redux';
import { appStore } from '../../store';
import { AppItem } from '../AppItems';

const searchThrottle: number = 200;

export const LocationInput = (props: Omit<TextFieldProps, 'value' | 'onChange'> & {
    value: ModelId | null,
    onChange: (id: ModelId | null) => void,
    onSetEmpty?: () => any;
}) => {

    const [inputValue, setInputValue] = React.useState('');
    const { value, onChange, onSetEmpty, ...restProps } = props;
    const [selectedValue, setSelectedValue] = React.useState<GoogleLocationInfo | null>();
    const [options, setOptions] = React.useState<GoogleLocationInfo[]>([]);

    const [editing, setEditing] = React.useState(false);
    const [autofocus, setAutofocus] = React.useState(false);

    React.useEffect(() => {
        setEditing(!props.value);
        setAutofocus(!!props.value);
    }, []);

    React.useEffect(() => {
        if (props.value) {
            setEditing(false);
        }
    }, [props.value]);

    const location = useSelector((state: AppState) => value ? locationSelector.selectById(state, value) : undefined);

    React.useEffect(() => {
        console.log('value', value);
        console.log('location', location);
        if (value && !location) {
            appStore.dispatch(fetchLocation(value));
        }
    }, [value, location]);

    React.useEffect(() => {
        location?.apiData && setOptions([location?.apiData]);
        setSelectedValue(location?.apiData ?? null);

    }, [location]);

    const getUrl = (query: string): string => {
        return '/location/findaddress?query=' + encodeURIComponent(query);
    };

    const fetch = React.useMemo(
        () =>
            debounce(
                async (
                    request: { input: string; },
                    callback: (results?: readonly GoogleLocationInfo[]) => void,
                ) => {
                    callback(await jsonGet<GoogleLocationInfo[]>(getUrl(request.input)));
                },
                searchThrottle,
            ),
        [],
    );


    const createLocation = async (googleLocation: GoogleLocationInfo) => {
        const newLocation: LocationAttrs = {
            ...brandNewLocation,
            lat: googleLocation.geometry.location.lat,
            lng: googleLocation.geometry.location.lng,
            latDelta: 0.02,
            lngDelta: 0.02,
            alt: null,
            apiData: googleLocation,
            authorId: '0',
            name: googleLocation?.formatted_address ?? 'Emplacement sur carte',
            placeId: googleLocation?.place_id ?? null
        };
        const response = await saveModel(AppModels.locations, newLocation);
        appStore.dispatch(locationSlice.actions.upsertOne(response));
        props.onChange(response.id);
    };

    React.useEffect(() => {
        let active = true;

        if (inputValue === '') {
            setOptions(selectedValue ? [selectedValue] : []);
            return undefined;
        }

        fetch({ input: inputValue }, (results?: readonly GoogleLocationInfo[]) => {
            if (active) {
                let newOptions: readonly GoogleLocationInfo[] = [];

                if (selectedValue) {
                    newOptions = [selectedValue];
                }

                if (results) {
                    newOptions = [...newOptions, ...results];
                }

                setOptions([...newOptions]);
            }
        });

        return () => {
            active = false;
        };

    }, [value, inputValue, fetch]);

    if (value && !location) return <></>;

    return editing ? <Autocomplete
        fullWidth
        id={Math.round(Math.random() * 999999999) + ''}
        getOptionLabel={(option: GoogleLocationInfo) => option?.formatted_address ?? ''}
        filterOptions={(x) => x}
        options={options}
        forcePopupIcon={false}
        autoComplete
        includeInputInList
        filterSelectedOptions
        value={selectedValue}
        noOptionsText="Rechercher une adresse ..."
        onChange={(_event: any, newValue: GoogleLocationInfo | null) => {
            console.log('Location changed', newValue);
            //setOptions(newValue ? [newValue, ...options] : options);
            setSelectedValue(newValue ?? null);
            newValue && createLocation(newValue);
        }}
        onInputChange={(_event, newInputValue) => {
            setInputValue(newInputValue);
        }}
        renderInput={(params) => (
            <TextField
                autoComplete='off'
                label="Adresse"
                {...restProps}
                {...params}
                InputProps={{
                    ...restProps.InputProps,
                    ...params.InputProps,
                    endAdornment: props.value ? <Button size='small' onClick={() => { setEditing(false); }}>Annuler</Button> : undefined
                }}
                autoFocus={autofocus} />
        )}
        renderOption={(props, option) => {
            return (
                <li {...props}>
                    <Grid container alignItems="center">
                        <Grid item sx={{ display: 'flex', width: 44 }}>
                            <LocationOn sx={{ color: 'text.secondary' }} />
                        </Grid>
                        <Grid item sx={{ width: 'calc(100% - 44px)', wordWrap: 'break-word' }}>
                            {option.name}
                            <Typography variant="body2" color="text.secondary">
                                {option.formatted_address}
                            </Typography>
                        </Grid>
                    </Grid>
                </li>
            );
        }}
    /> : <FormControl fullWidth>
        <InputLabel shrink>{props.label ?? 'Adresse'}</InputLabel>
        <Box sx={{ display: 'flex', flexDirection: 'row', height: 56, paddingLeft: 1, alignItems: 'center' }}>
            <Box sx={{ flexGrow: 1 }}>
                {props.value ? <AppItem mode='compact' type={AppModels.locations} id={props.value} /> : null}
            </Box>
            <Box sx={{ flexShrink: 1 }}>
                <IconButton onClick={() => { setEditing(true); }}><Edit /></IconButton>
                {props.onSetEmpty ? <IconButton onClick={() => {
                    props.onSetEmpty && props.onSetEmpty();
                    setEditing(true);
                }}><Delete /></IconButton> : null}
            </Box>
        </Box>
    </FormControl>;
};
